import { Badge } from "flowbite-react";
import { CalendarRange, ShoppingBag, Tag } from "lucide-react";
import { CART_TYPE_LABELS } from "@/shared/constants/cart.constants";
import type { CartType } from "@/shared/types/unified-cart.type";
import { withVisibleThousands } from "@/shared/utils/formatEuro";

type CartTypeSelectorProps = {
  activeType: CartType
  onTypeChange: (type: CartType) => void
  boutiqueCount: number
  dealsCount: number
  rentalsCount?: number
  boutiqueSubtotal?: number
  dealsSubtotal?: number
  rentalsSubtotal?: number
  variant?: "header" | "page"
  showDealsTab?: boolean
  showRentalsTab?: boolean
};

const formatCurrency = (amount: number): string =>
  withVisibleThousands(amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));

const formatItemCount = (count: number): string =>
  count === 1 ? "1 article" : `${count} articles`;

const getCartSubtitle = (count: number, subtotal?: number): string => {
  if (count === 0) {
    return "Panier vide";
  }
  const itemText = formatItemCount(count);
  return subtotal !== undefined ? `${itemText} • ${formatCurrency(subtotal)}` : itemText;
};

const ACTIVE_BUTTON_CLASSES =
  "bg-white dark:bg-gray-700 border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300 shadow-sm ring-2 ring-primary-100 dark:ring-primary-900";

const INACTIVE_BUTTON_CLASSES =
  "bg-transparent border-transparent text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50";

const HEADER_ACTIVE_CLASSES =
  "bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300";

const HEADER_INACTIVE_CLASSES =
  "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700";

type SegmentButtonProps = {
  type: CartType
  isActive: boolean
  count: number
  subtotal?: number
  onClick: () => void
  icon: React.ReactNode
};

const SegmentButton = ({
  type,
  isActive,
  count,
  subtotal,
  onClick,
  icon,
}: SegmentButtonProps) => {
  const buttonClasses = isActive ? ACTIVE_BUTTON_CLASSES : INACTIVE_BUTTON_CLASSES;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`flex flex-1 flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all duration-200 ${buttonClasses}`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-semibold">{CART_TYPE_LABELS[type]}</span>
        {count > 0 && (
          <Badge color={isActive ? "info" : "gray"} size="sm">
            {count}
          </Badge>
        )}
      </div>
      <span className="text-sm opacity-80">
        {getCartSubtitle(count, subtotal)}
      </span>
    </button>
  );
};

const HeaderVariant = ({
  activeType,
  onTypeChange,
  boutiqueCount,
  dealsCount,
  rentalsCount = 0,
  showDealsTab = true,
  showRentalsTab = true,
}: Pick<CartTypeSelectorProps,
| "activeType" | "onTypeChange"
| "boutiqueCount" | "dealsCount" | "rentalsCount"
| "showDealsTab" | "showRentalsTab"
>) => {
  if (!showDealsTab && !showRentalsTab) {
    return null;
  }

  const tabs: Array<{
    type: CartType
    label: string
    icon: React.ReactNode
    count: number
    visible: boolean
  }> = [
    {
      type: "boutique", label: CART_TYPE_LABELS.boutique, icon: <ShoppingBag size={16} />, count: boutiqueCount, visible: true,
    },
    {
      type: "deals", label: CART_TYPE_LABELS.deals, icon: <Tag size={16} />, count: dealsCount, visible: showDealsTab,
    },
    {
      type: "rentals", label: CART_TYPE_LABELS.rentals, icon: <CalendarRange size={16} />, count: rentalsCount, visible: showRentalsTab,
    },
  ];

  return (
    <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      {tabs.filter((t) => t.visible).map((tab) => {
        const isActive = activeType === tab.type;
        return (
          <button
            key={tab.type}
            type="button"
            onClick={() => onTypeChange(tab.type)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? HEADER_ACTIVE_CLASSES : HEADER_INACTIVE_CLASSES
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.count > 0 && (
              <Badge color={isActive ? "info" : "gray"} size="sm">
                {tab.count}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Segmented control component to switch between boutique / deals / rentals
 * cart views. Tabs show only when their feature is enabled
 * (`showDealsTab` / `showRentalsTab`).
 */
export const CartTypeSelector = ({
  activeType,
  onTypeChange,
  boutiqueCount,
  dealsCount,
  rentalsCount = 0,
  boutiqueSubtotal,
  dealsSubtotal,
  rentalsSubtotal,
  variant = "page",
  showDealsTab = true,
  showRentalsTab = true,
}: CartTypeSelectorProps) => {
  if (variant === "header") {
    return (
      <HeaderVariant
        activeType={activeType}
        onTypeChange={onTypeChange}
        boutiqueCount={boutiqueCount}
        dealsCount={dealsCount}
        rentalsCount={rentalsCount}
        showDealsTab={showDealsTab}
        showRentalsTab={showRentalsTab}
      />
    );
  }

  if (!showDealsTab && !showRentalsTab) {
    return null;
  }

  const tabs: Array<{
    type: CartType
    icon: React.ReactNode
    count: number
    subtotal?: number
    visible: boolean
  }> = [
    {
      type: "boutique", icon: <ShoppingBag size={20} />, count: boutiqueCount, subtotal: boutiqueSubtotal, visible: true,
    },
    {
      type: "deals", icon: <Tag size={20} />, count: dealsCount, subtotal: dealsSubtotal, visible: showDealsTab,
    },
    {
      type: "rentals", icon: <CalendarRange size={20} />, count: rentalsCount, subtotal: rentalsSubtotal, visible: showRentalsTab,
    },
  ];

  return (
    <div
      className="flex gap-3 rounded-xl bg-gray-100 p-2 dark:bg-gray-800"
      role="group"
      aria-label="Sélection du type de panier"
    >
      {tabs.filter((t) => t.visible).map((tab) => (
        <SegmentButton
          key={tab.type}
          type={tab.type}
          isActive={activeType === tab.type}
          count={tab.count}
          subtotal={tab.subtotal}
          onClick={() => onTypeChange(tab.type)}
          icon={tab.icon}
        />
      ))}
    </div>
  );
};

CartTypeSelector.displayName = "CartTypeSelector";
